/**
 * FormCard Component
 *
 * Card wrapper for forms with consistent styling:
 * - RoundedCard container
 * - Optional title and subtitle
 * - Consistent padding and spacing
 */

import { ReactNode } from 'react';
import {
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { RoundedCard } from '../../common/RoundedCard';

const useStyles = makeStyles({
  card: {
    ...shorthands.padding('24px'),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
    marginBottom: '24px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
});

export interface FormCardProps {
  /** Optional card title */
  title?: string;
  /** Optional card subtitle/description */
  subtitle?: string;
  /** Form content */
  children: ReactNode;
  /** Test ID for the component */
  testId?: string;
}

export function FormCard({ title, subtitle, children, testId }: FormCardProps) {
  const styles = useStyles();
  const hasHeader = title || subtitle;

  return (
    <RoundedCard className={styles.card} data-testid={testId}>
      {hasHeader && (
        <div className={styles.header}>
          {title && <Text className={styles.title}>{title}</Text>}
          {subtitle && <Text className={styles.subtitle}>{subtitle}</Text>}
        </div>
      )}
      <div className={styles.content}>{children}</div>
    </RoundedCard>
  );
}
